import { useState, useRef, useEffect, memo, KeyboardEvent } from 'react'
import { TypedStorage } from '../utils/storage'
import type { CommandEntry, CommandOutput } from '../types/commands'

interface TerminalInputProps {
  onExecute: (input: string) => Promise<void>
  getAutocomplete: (partial: string) => string[]
  addHistoryEntry: (entry: CommandEntry) => void
  isProcessing: boolean
  currentDirectory: string
}

const findCommonPrefix = (strings: string[]): string => {
  if (strings.length === 0) return ''
  if (strings.length === 1) return strings[0]

  let prefix = strings[0]
  for (let i = 1; i < strings.length; i++) {
    while (strings[i].indexOf(prefix) !== 0) {
      prefix = prefix.substring(0, prefix.length - 1)
      if (prefix === '') return ''
    }
  }
  return prefix
}

const TerminalInput = memo(({
  onExecute,
  getAutocomplete,
  addHistoryEntry,
  isProcessing,
  currentDirectory,
}: TerminalInputProps) => {
  const [currentInput, setCurrentInput] = useState('')
  const [commandHistory, setCommandHistory] = useState<string[]>(() =>
    TypedStorage.get('terminal-command-history', [])
  )
  const [historyIndex, setHistoryIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Debounced persistence of command history
  useEffect(() => {
    clearTimeout(persistTimerRef.current)
    persistTimerRef.current = setTimeout(() => {
      TypedStorage.set('terminal-command-history', commandHistory)
    }, 500)
    return () => clearTimeout(persistTimerRef.current)
  }, [commandHistory])

  // Refocus after processing completes
  useEffect(() => {
    if (!isProcessing) {
      inputRef.current?.focus()
    }
  }, [isProcessing])

  const handleEnterKey = () => {
    if (!currentInput.trim() || isProcessing) return
    const input = currentInput
    setCommandHistory((prev) => [...prev, input])
    setHistoryIndex(-1)
    setCurrentInput('')
    onExecute(input)
  }

  const handleEscapeKey = () => {
    setCurrentInput('')
    setHistoryIndex(-1)
  }

  const handleHistoryNavigation = (isUp: boolean) => {
    if (isUp) {
      if (commandHistory.length === 0) return
      const newIndex = historyIndex + 1
      if (newIndex < commandHistory.length) {
        setHistoryIndex(newIndex)
        setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex])
      }
    } else {
      if (historyIndex <= 0) {
        setHistoryIndex(-1)
        setCurrentInput('')
        return
      }
      const newIndex = historyIndex - 1
      setHistoryIndex(newIndex)
      setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex])
    }
  }

  const handleTabAutocomplete = () => {
    const suggestions = getAutocomplete(currentInput)

    if (suggestions.length === 0) return

    if (suggestions.length === 1) {
      const suggestion = suggestions[0]
      setCurrentInput(suggestion.endsWith('/') ? suggestion : suggestion + ' ')
      return
    }

    const commonPrefix = findCommonPrefix(suggestions)

    if (commonPrefix.length > currentInput.length) {
      setCurrentInput(commonPrefix)
    } else {
      const isPathCompletion = suggestions[0].includes(' ')
      const label = isPathCompletion ? 'Available paths:' : 'Available commands:'
      const output: CommandOutput[] = [
        {
          type: 'info',
          content: `\n${label}\n${suggestions.map(s => s.includes(' ') ? s.split(' ')[1] : s).join('  ')}\n`,
        },
      ]
      addHistoryEntry({
        input: currentInput,
        output,
        timestamp: new Date(),
      })
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEnterKey()
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      handleEscapeKey()
      return
    }

    const isUp = e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')
    const isDown = e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')
    if (isUp || isDown) {
      e.preventDefault()
      handleHistoryNavigation(isUp)
      return
    }

    if (e.key === 'Tab') {
      e.preventDefault()
      handleTabAutocomplete()
    }
  }

  return (
    <>
      <div className="command-prompt" role="form" aria-label="Command input form">
        <label htmlFor="terminal-input" className="sr-only">
          Enter terminal command
        </label>
        <span className="prompt" aria-hidden="true">{currentDirectory}$</span>
        <input
          id="terminal-input"
          ref={inputRef}
          type="text"
          className="command-input"
          value={currentInput}
          onChange={(e) => setCurrentInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isProcessing ? 'Processing...' : 'Type a command...'}
          disabled={isProcessing}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          aria-label="Terminal command input"
          aria-describedby="terminal-help"
          aria-busy={isProcessing}
        />
        <span id="terminal-help" className="sr-only">
          Type 'help' to see available commands. Use arrow keys or Ctrl+P/Ctrl+N to navigate command history. Press Tab for autocomplete. Press Escape to clear input.
        </span>
        {isProcessing && <span className="cursor-blink" aria-hidden="true">|</span>}
      </div>
    </>
  )
})

TerminalInput.displayName = 'TerminalInput'

export default TerminalInput
